import { Group, ScrollArea, Stack } from '@mantine/core';
import { useNavigate } from 'react-router';
import { InitiativeSource } from '../../../../api/initiative-client/initiative-client.type';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useStrategyStore } from '../../../../store/strategy-store/strategy-store';
import {
  resetRelatedTasksTabState,
  setActivePanel,
  setSelectedInitiative,
} from '../../../../store/strategy-store/strategy-store.actions';
import { baseWhite, primaryBase, purpleBase, secondaryBase, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Divider } from '../../../../ui-library/divider/divider';
import { Icon } from '../../../../ui-library/icon/icon';
import { Loader } from '../../../../ui-library/loader/loader';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { usePartiallyUpdateInitiative } from '../../initiative-performance/initiative-performance.hooks';
import { getSourceIcon } from '../main-section/main-section.helpers';
import { SideSectionInfo } from './side-section-info';
import { ProgressBar } from './side-section-progress';

export const SideSectionSelectedInitiative = () => {
  const i18n = useTranslation();
  const navigate = useNavigate();

  const initiative = useStrategyStore((state) => state.selectedInitiative);
  const portfolio = useGlobalStore((state) => state.portfolio);

  const editInitiativeMutation = usePartiallyUpdateInitiative({
    portfolioId: portfolio?.id,
    initiativeId: initiative?.id,
  });

  if (!initiative || !portfolio) {
    return null;
  }

  const handleKeyClick = () => {
    if (initiative.source !== InitiativeSource.Bloomfilter && initiative.url) {
      window.open(initiative.url, '_blank', 'noopener, noreferrer');
    }
  };

  return (
    <Stack h="100%">
      <Group justify={initiative.key ? 'space-between' : 'end'}>
        {initiative.key && (
          <Group
            gap={4}
            style={{
              cursor: initiative.source !== InitiativeSource.Bloomfilter && initiative.url ? 'pointer' : 'default',
            }}
            onClick={handleKeyClick}
          >
            <img src={getSourceIcon(initiative.source)} width={16} height={16} alt={initiative.source} />
            <Text weight="bold" size="medium" color={secondaryBase}>
              {initiative.key}
            </Text>
            {initiative.source !== InitiativeSource.Bloomfilter && (
              <Tooltip variant="white" label={i18n.t('common.visit_source_page')} position="right">
                <Icon name="arrow_outward" size={16} color={secondaryBase} pointer />
              </Tooltip>
            )}
          </Group>
        )}
        <Group gap={4}>
          {editInitiativeMutation.isPending ? (
            <Loader size="xs" />
          ) : (
            <Icon
              name={initiative.is_bookmarked ? 'bookmark_added' : 'bookmark_add'}
              size={20}
              color={initiative.is_bookmarked ? secondaryBase : skyDark}
              pointer
              onClick={() => {
                editInitiativeMutation.mutateAsync({ is_bookmarked: !initiative.is_bookmarked });
              }}
            />
          )}

          <Icon
            name="close"
            onClick={() => {
              setSelectedInitiative(null);
              setActivePanel(null);
            }}
            size={20}
            color={skyDark}
            aria-label="Close panel"
          />
        </Group>
      </Group>
      <Divider />
      <ScrollArea h="100%" scrollbarSize={0}>
        <Stack>
          <Text weight="medium" size="medium">
            {initiative.name}
          </Text>
          <Divider />
          <ProgressBar
            value={initiative.percent_progress}
            label={i18n.t('strategy.initiatives.table.completion')}
            color={primaryBase}
          />
          <ProgressBar
            value={initiative.percent_elapsed}
            label={i18n.t('strategy.initiative_performance.time_elapsed')}
            color={purpleBase}
          />
          <SideSectionInfo entity={initiative} entityType="initiative" />
          <Divider />
        </Stack>
      </ScrollArea>
      <Button
        variant="secondary"
        size="xs"
        radius="xl"
        fullWidth
        leftSection={<Icon name="remove_red_eye" variant="filled" color={baseWhite} size={14} pointer />}
        onClick={() => {
          resetRelatedTasksTabState();
          navigate(`/application/strategy/${portfolio.id}/${initiative.id}`);
        }}
      >
        <Text size="small" color={baseWhite}>
          {i18n.t('strategy.initiatives.more_initiative_details')}
        </Text>
      </Button>
    </Stack>
  );
};
